// src/utils/qr-firebase-utils.js
import {
    collection,
    addDoc,
    getDocs,
    query,
    where,
    orderBy,
    limit,
    serverTimestamp,
    doc,
    updateDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';

export const QRFirebaseUtils = {
    // Save QR code data to Firestore
    saveQRCode: async (qrCodeData) => {
        try {
            const docRef = await addDoc(collection(db, 'qr-codes'), {
                ...qrCodeData,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                status: 'active'
            });
            return docRef.id;
        } catch (error) {
            console.error('Error saving QR code:', error);
            throw error;
        }
    },

    // Get QR code data by UUID
    getQRCodeByUUID: async (uuid) => {
        try {
            const q = query(collection(db, 'qr-codes'), where('uuid', '==', uuid));
            const querySnapshot = await getDocs(q);

            if (querySnapshot.empty) {
                return null;
            }

            const doc = querySnapshot.docs[0];
            return {
                id: doc.id,
                ...doc.data()
            };
        } catch (error) {
            console.error('Error fetching QR code by UUID:', error);
            throw error;
        }
    },

    // Get all QR codes
    getAllQRCodes: async () => {
        try {
            const q = query(collection(db, 'qr-codes'), orderBy('createdAt', 'desc'));
            const querySnapshot = await getDocs(q);
            return querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
        } catch (error) {
            console.error('Error fetching all QR codes:', error);
            throw error;
        }
    },

    // Get QR codes by box ID
    getQRCodesByBoxId: async (boxId) => {
        try {
            const q = query(
                collection(db, 'qr-codes'),
                where('boxId', '==', boxId),
                orderBy('createdAt', 'desc')
            );
            const querySnapshot = await getDocs(q);
            return querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
        } catch (error) {
            console.error('Error fetching QR codes by box ID:', error);
            throw error;
        }
    },

    // Get QR codes by donor
    getQRCodesByDonor: async (donorName) => {
        try {
            const q = query(
                collection(db, 'qr-codes'),
                where('donorName', '==', donorName),
                orderBy('createdAt', 'desc')
            );
            const querySnapshot = await getDocs(q);
            return querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
        } catch (error) {
            console.error('Error fetching QR codes by donor:', error);
            throw error;
        }
    },

    // Update QR code status (e.g., mark as used)
    updateQRCodeStatus: async (uuid, status, additionalData = {}) => {
        try {
            const qrCode = await QRFirebaseUtils.getQRCodeByUUID(uuid);
            if (!qrCode) {
                throw new Error('QR code not found');
            }

            const docRef = doc(db, 'qr-codes', qrCode.id);
            await updateDoc(docRef, {
                status,
                ...additionalData,
                updatedAt: serverTimestamp()
            });
            return true;
        } catch (error) {
            console.error('Error updating QR code status:', error);
            throw error;
        }
    },

    // Save donation with QR code reference
    saveDonationWithQR: async (donationData) => {
        try {
            const docRef = await addDoc(collection(db, 'donations'), {
                ...donationData,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });

            if (donationData.qrUUID && donationData.qrUUID.trim() !== '') {
                await QRFirebaseUtils.updateQRCodeStatus(
                    donationData.qrUUID,
                    'used',
                    {
                        donationId: docRef.id,
                        collectedAt: serverTimestamp(),
                        collectedBy: donationData.volunteerName,
                    }
                );
            }

            return docRef.id;
        } catch (error) {
            console.error('Error saving donation with QR:', error);
            throw error;
        }
    },

    // Get donations by QR code
    getDonationsByQR: async (qrUUID) => {
        try {
            const q = query(
                collection(db, 'donations'),
                where('qrUUID', '==', qrUUID),
                orderBy('createdAt', 'desc')
            );
            const querySnapshot = await getDocs(q);
            return querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
        } catch (error) {
            console.error('Error fetching donations by QR:', error);
            throw error;
        }
    },

    // Get QR code statistics
    getQRCodeStats: async () => {
        try {
            const qrCodes = await QRFirebaseUtils.getAllQRCodes();

            const stats = {
                totalQRCodes: qrCodes.length,
                activeQRCodes: 0,
                usedQRCodes: 0,
                qrCodesByBox: {},
                qrCodesByStatus: {},
                recentQRCodes: []
            };

            qrCodes.forEach(qr => {
                // By status
                if (!stats.qrCodesByStatus[qr.status]) {
                    stats.qrCodesByStatus[qr.status] = 0;
                }
                stats.qrCodesByStatus[qr.status]++;

                if (qr.status === 'active') stats.activeQRCodes++;
                if (qr.status === 'used') stats.usedQRCodes++;

                // By box
                if (qr.boxId) {
                    if (!stats.qrCodesByBox[qr.boxId]) {
                        stats.qrCodesByBox[qr.boxId] = 0;
                    }
                    stats.qrCodesByBox[qr.boxId]++;
                }
            });

            // Recent QR codes (last 10)
            stats.recentQRCodes = qrCodes.slice(0, 10);

            return stats;
        } catch (error) {
            console.error('Error calculating QR code stats:', error);
            throw error;
        }
    },

    // Generate unique UUID
    generateUUID: () => {
        return 'qr-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
    },

    // Validate QR code data
    validateQRCodeData: (qrData) => {
        const required = ['boxId', 'location', 'donorName', 'donorMobile'];
        const missing = required.filter(field => !qrData[field]);

        if (missing.length > 0) {
            throw new Error(`Missing required fields: ${missing.join(', ')}`);
        }

        // Validate mobile number format (basic validation)
        if (qrData.donorMobile && !/^\d{10}$/.test(qrData.donorMobile.replace(/\D/g, ''))) {
            throw new Error('Invalid mobile number format');
        }

        // Validate PAN format if provided
        if (qrData.donorPan && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(qrData.donorPan.toUpperCase())) {
            throw new Error('Invalid PAN format');
        }

        return true;
    },

    // Search QR codes
    searchQRCodes: async (searchTerm) => {
        try {
            // This is a basic search - for more complex search, consider using Algolia
            const allQRCodes = await QRFirebaseUtils.getAllQRCodes();

            const searchLower = searchTerm.toLowerCase();
            return allQRCodes.filter(qr =>
                qr.donorName?.toLowerCase().includes(searchLower) ||
                qr.donorMobile?.includes(searchTerm) ||
                qr.boxId?.toLowerCase().includes(searchLower) ||
                qr.location?.toLowerCase().includes(searchLower) ||
                qr.uuid?.includes(searchTerm)
            );
        } catch (error) {
            console.error('Error searching QR codes:', error);
            throw error;
        }
    }
};

// Extended Firebase utilities for donations
export const DonationFirebaseUtils = {
    // Get all donations
    getAllDonations: async () => {
        try {
            const q = query(collection(db, 'donations'), orderBy('createdAt', 'desc'));
            const querySnapshot = await getDocs(q);
            return querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
        } catch (error) {
            console.error('Error fetching donations:', error);
            throw error;
        }
    },

    // Get donations with QR code data joined
    getDonationsWithQRData: async () => {
        try {
            const donations = await DonationFirebaseUtils.getAllDonations();
            const donationsWithQR = [];

            for (const donation of donations) {
                if (donation.qrUUID) {
                    const qrData = await QRFirebaseUtils.getQRCodeByUUID(donation.qrUUID);
                    donationsWithQR.push({
                        ...donation,
                        qrCodeInfo: qrData
                    });
                } else {
                    donationsWithQR.push(donation);
                }
            }

            return donationsWithQR;
        } catch (error) {
            console.error('Error fetching donations with QR data:', error);
            throw error;
        }
    },

    // Get donation statistics
    getDonationStats: async () => {
        try {
            const donations = await DonationFirebaseUtils.getAllDonations();

            const stats = {
                totalDonations: donations.length,
                totalAmount: donations.reduce((sum, donation) => sum + (donation.totalAmount || 0), 0),
                donationsFromQR: donations.filter(d => d.qrUUID).length,
                donationsManual: donations.filter(d => !d.qrUUID).length,
                donationsByVolunteer: {},
                donationsByPurpose: {},
                donationsByBox: {},
                averageDonation: 0
            };

            donations.forEach(donation => {
                // By volunteer
                if (donation.volunteerName) {
                    if (!stats.donationsByVolunteer[donation.volunteerName]) {
                        stats.donationsByVolunteer[donation.volunteerName] = {
                            count: 0,
                            amount: 0,
                            fromQR: 0,
                            manual: 0
                        };
                    }
                    stats.donationsByVolunteer[donation.volunteerName].count++;
                    stats.donationsByVolunteer[donation.volunteerName].amount += donation.totalAmount || 0;

                    if (donation.qrUUID) {
                        stats.donationsByVolunteer[donation.volunteerName].fromQR++;
                    } else {
                        stats.donationsByVolunteer[donation.volunteerName].manual++;
                    }
                }

                // By purpose
                if (donation.purpose) {
                    if (!stats.donationsByPurpose[donation.purpose]) {
                        stats.donationsByPurpose[donation.purpose] = { count: 0, amount: 0 };
                    }
                    stats.donationsByPurpose[donation.purpose].count++;
                    stats.donationsByPurpose[donation.purpose].amount += donation.totalAmount || 0;
                }

                // By box
                if (donation.boxId) {
                    if (!stats.donationsByBox[donation.boxId]) {
                        stats.donationsByBox[donation.boxId] = { count: 0, amount: 0 };
                    }
                    stats.donationsByBox[donation.boxId].count++;
                    stats.donationsByBox[donation.boxId].amount += donation.totalAmount || 0;
                }
            });

            stats.averageDonation = stats.totalDonations > 0 ? stats.totalAmount / stats.totalDonations : 0;

            return stats;
        } catch (error) {
            console.error('Error calculating donation stats:', error);
            throw error;
        }
    }
};